import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { AdminRole, AdminUser } from '../../types'
import {
  requestCreateRole,
  requestCreateUser,
  requestUpdateUserRoles,
} from './securityTeamApi'
import { runAsyncAction } from './asyncActionFeedback'

type Translator = (key: string, params?: Record<string, string | number>) => string

type TeamMutationSetters = {
  setUsers: Dispatch<SetStateAction<AdminUser[]>>
  setRoles: Dispatch<SetStateAction<AdminRole[]>>
}

export const useSecurityTeamMutations = ({
  authHeaders,
  t,
  ensureAuthenticated,
  setters,
}: {
  authHeaders: (withJson?: boolean) => Record<string, string>
  t: Translator
  ensureAuthenticated: () => boolean
  setters: TeamMutationSetters
}) => {
  const runMutation = useCallback(
    async <TResult>({
      action,
      fallbackErrorKey,
      successKey,
      onSuccess,
    }: {
      action: () => Promise<TResult>
      fallbackErrorKey: string
      successKey: string
      onSuccess: (result: TResult) => void
    }): Promise<TResult | null> => {
      if (!ensureAuthenticated()) return null
      const result = await runAsyncAction({
        action,
        fallbackErrorMessage: t(fallbackErrorKey),
        onErrorMessage: (message) => toast.error(message),
      })
      if (!result) return null

      onSuccess(result)
      toast.success(t(successKey))
      return result
    },
    [ensureAuthenticated, t],
  )

  const createUser = useCallback(
    async (payload: {
      username: string
      password: string
      displayName?: string
      email?: string
      roles: string[]
    }) => {
      return runMutation({
        action: () => requestCreateUser({ authHeaders, payload, t }),
        fallbackErrorKey: 'security.team.createError',
        successKey: 'security.team.created',
        onSuccess: (user) => {
          if (user.id) {
            setters.setUsers((prev) => [user, ...prev.filter((item) => item.id !== user.id)])
          }
        },
      })
    },
    [authHeaders, runMutation, setters, t],
  )

  const updateUserRoles = useCallback(
    async (
      id: string,
      payload: { roles: string[]; displayName?: string; email?: string; password?: string },
    ) => {
      return runMutation({
        action: () => requestUpdateUserRoles({ authHeaders, id, payload, t }),
        fallbackErrorKey: 'security.team.rolesError',
        successKey: 'security.team.rolesUpdated',
        onSuccess: (user) => {
          if (user.id) {
            setters.setUsers((prev) => prev.map((item) => (item.id === user.id ? user : item)))
          }
        },
      })
    },
    [authHeaders, runMutation, setters, t],
  )

  const createRole = useCallback(
    async (payload: { label: string; name?: string; description?: string }) => {
      return runMutation({
        action: () => requestCreateRole({ authHeaders, payload, t }),
        fallbackErrorKey: 'security.team.createRoleError',
        successKey: 'security.team.roleCreated',
        onSuccess: (role) => {
          if (role.id) {
            setters.setRoles((prev) => [...prev, role])
          }
        },
      })
    },
    [authHeaders, runMutation, setters, t],
  )

  return {
    createUser,
    updateUserRoles,
    createRole,
  }
}
